import React, { useMemo, useState } from 'react';

import {
  IContaDoPlano,
  IDemonstracoes,
  IDemonstracoesDoPeriodo,
  IGruposDoBalanco,
  ILinhaDoModelo,
  useDemonstracoesDosAnos,
} from '../../../../../../hooks/useFechamentoContabilMensal';
import { mesLongo } from '../../regras';
import {
  IColuna,
  IColunasDoPeriodo,
  IPedaco,
  IPosicao,
  montarColunas,
  totalComparavel,
} from '../../regrasDasDemonstracoes';
import { num, numCurto, saldoDoBalanco, textoDaContaOu } from '../../regrasDoDetalhe';
import { IPeriodo, PERIODICIDADES } from '../../regrasDoPeriodoDaEmpresa';

type Aba = 'dre' | 'ebitda' | 'bp' | 'dfc' | 'evol';
type Modo = 'valor' | 'pct';
type Plano = Record<string, IContaDoPlano> | undefined;

const ABAS: [Aba, string][] = [
  ['dre', 'Resultado (DRE)'],
  ['ebitda', 'EBITDA'],
  ['bp', 'Balanço patrimonial'],
  ['dfc', 'Fluxo de caixa'],
  ['evol', 'Evolução mensal'],
];

const SECAO: React.CSSProperties = {
  minWidth: 0,
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  background: 'rgba(255, 255, 255, .62)',
  border: '1px solid rgba(255, 255, 255, .8)',
  borderRadius: 16,
  boxShadow: '0 8px 28px rgba(21, 95, 176, .10)',
  backdropFilter: 'blur(14px)',
};
const H2: React.CSSProperties = { margin: 0, fontSize: 14, fontWeight: 700 };
const SUB: React.CSSProperties = { fontSize: 11.5, color: '#52525B' };
const CABECA: React.CSSProperties = {
  color: '#52525B',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '.05em',
};
const VERMELHO = '#B91C1C';
const CORES = ['#1B88F5', '#16A34A', '#DC2626'];

const MEDIA: Record<string, string> = {
  quinzenal: 'Média quinzenal',
  mensal: 'Média mensal',
  bimestral: 'Média bimestral',
  trimestral: 'Média trimestral',
  semestral: 'Média semestral',
  anual: 'Média anual',
};

/** Linhas da DRE na ordem da tela; `total_op` é a soma das despesas operacionais. */
const DRE: [string, string, boolean][] = [
  ['receita_bruta', 'Receita bruta', false],
  ['deducoes', '(−) Deduções e impostos s/ vendas', false],
  ['receita_liquida', 'Receita líquida', true],
  ['custos', '(−) Custo das vendas', false],
  ['lucro_bruto', 'Lucro bruto', true],
  ['despesas_pessoal', '(−) Pessoal', false],
  ['despesas_operacionais', '(−) Despesas operacionais', false],
  ['despesas_tributarias', '(−) Despesas tributárias', false],
  ['depreciacao', '(−) Depreciação', false],
  ['outras_despesas', '(−) Outras despesas', false],
  ['total_op', 'Total das despesas operacionais', true],
  ['outras_receitas', 'Outras receitas', false],
  ['resultado_operacional', 'Resultado operacional', true],
  ['receitas_financeiras', 'Receitas financeiras', false],
  ['despesas_financeiras', '(−) Despesas financeiras', false],
  ['resultado_antes_ir', 'Resultado antes do IR/CSLL', true],
  ['ir_csll', '(−) IR/CSLL', false],
  ['lucro_liquido', 'Lucro líquido', true],
];
const OPERACIONAIS = [
  'despesas_pessoal',
  'despesas_operacionais',
  'despesas_tributarias',
  'depreciacao',
  'outras_despesas',
];

/** Valor de uma linha da DRE num conjunto somado (coluna ou pedaço). */
const linhaDaDre = (dre: Record<string, number>, k: string): number =>
  k === 'total_op' ? OPERACIONAIS.reduce((s, x) => s + (dre[x] || 0), 0) : dre[k] || 0;

const ebitdaDe = (dre: Record<string, number>) =>
  linhaDaDre(dre, 'lucro_liquido') -
  linhaDaDre(dre, 'ir_csll') -
  linhaDaDre(dre, 'receitas_financeiras') -
  linhaDaDre(dre, 'despesas_financeiras') -
  linhaDaDre(dre, 'depreciacao');

const pct = (v: number) =>
  `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const cor = (v: number | null | undefined) => (v != null && v < -0.004 ? VERMELHO : '#0F172A');
const soma = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

interface ICelula {
  t: string;
  cor: string;
  fundo?: string;
}

const varPct = (a: number, b: number): ICelula => {
  if (!b) return { t: '—', cor: '#52525B' };
  const r = (a - b) / Math.abs(b);
  let seta = '';
  if (r > 0.0005) seta = '▲ ';
  else if (r < -0.0005) seta = '▼ ';
  let c = '#52525B';
  if (r > 0.0005) c = '#15803D';
  else if (r < -0.0005) c = VERMELHO;
  return { t: seta + pct(Math.abs(r)), cor: c };
};

const varPP = (d: number): ICelula => {
  let sinal = '';
  if (d > 0.05) sinal = '+';
  else if (d < -0.05) sinal = '−';
  return {
    t: `${sinal}${Math.abs(d).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} p.p.`,
    cor: '#52525B',
  };
};

// ------------------------------------------------------------------ estrutura das colunas

interface IGrade {
  c: IColunasDoPeriodo;
  anos: number[];
  multi: boolean;
  /** cabeçalhos antes dos períodos: acumulado e média, ou total de cada ano e a variação */
  cabecas: { nome: string; fundo: string; cor: string }[];
  /** índices das colunas, da mais recente para a mais antiga */
  ordem: number[];
  largura: string;
  fundo: (j: number) => string;
}

const montarGrade = (c: IColunasDoPeriodo, periodo: IPeriodo): IGrade => {
  const anos = periodo.anos.filter(a => c.colunas.some(x => x.ano === a)).reverse();
  const multi = anos.length > 1;
  const yy = (a: number) => String(a).slice(2);
  const cabecas = multi
    ? anos
        .map(a => ({ nome: `Total ${a}`, fundo: '#DCEBFB', cor: '#155FB0' }))
        .concat([
          { nome: `Var. ${yy(anos[0])} × ${yy(anos[1])}`, fundo: '#FCF1DD', cor: '#B45309' },
        ])
    : [
        { nome: 'Acumulado', fundo: '#DCEBFB', cor: '#155FB0' },
        { nome: MEDIA[c.visao], fundo: '#E8F1FB', cor: '#155FB0' },
      ];
  const n = cabecas.length;
  return {
    c,
    anos,
    multi,
    cabecas,
    ordem: c.colunas.map((_, j) => c.colunas.length - 1 - j),
    largura: `${260 + 112 * (c.colunas.length + n)}px`,
    fundo: (j: number) => {
      if (j >= n) return 'transparent';
      if (multi) return j === n - 1 ? 'rgba(217, 119, 6, .07)' : 'rgba(27, 111, 208, .07)';
      return j === 0 ? 'rgba(27, 111, 208, .07)' : 'rgba(27, 111, 208, .035)';
    },
  };
};

/**
 * Células de uma linha: cabeçalhos (acumulado e média, ou total por ano e variação) e
 * cada período. `porPedaco` dá o valor comparável entre anos.
 */
const celulasDaLinha = (
  g: IGrade,
  serie: number[],
  receita: number[],
  modo: Modo,
  porPedaco: ((p: IPedaco) => number) | null,
  receitaPorPedaco: (p: IPedaco) => number,
  formato?: (v: number, base: number) => string,
): ICelula[] => {
  const { c, multi, anos, cabecas, ordem } = g;
  const n = cabecas.length;
  const cel = (v: number, base: number, j: number): ICelula => {
    let t: string;
    if (formato) t = formato(v, base);
    else if (modo === 'pct') t = base ? pct(v / base) : '—';
    else t = num(v);
    return { t, cor: cor(v), fundo: g.fundo(j) };
  };
  const totalDoAno = (s: number[], ano: number) =>
    soma(c.colunas.map((col, j) => (col.ano === ano ? s[j] : 0)));
  let cab: ICelula[];
  if (multi) {
    const [a, b] = anos;
    let variacao: ICelula = { t: '—', cor: '#52525B' };
    if (porPedaco) {
      const va = totalComparavel(c.colunas, a, c.mesesComuns, porPedaco);
      const vb = totalComparavel(c.colunas, b, c.mesesComuns, porPedaco);
      if (modo === 'pct' && !formato) {
        const ra = totalComparavel(c.colunas, a, c.mesesComuns, receitaPorPedaco);
        const rb = totalComparavel(c.colunas, b, c.mesesComuns, receitaPorPedaco);
        variacao = ra && rb ? varPP((va / ra - vb / rb) * 100) : { t: '—', cor: '#52525B' };
      } else variacao = varPct(va, vb);
    }
    cab = anos
      .map((ano, j) => cel(totalDoAno(serie, ano), totalDoAno(receita, ano), j))
      .concat([{ ...variacao, fundo: g.fundo(n - 1) }]);
  } else {
    const tot = soma(serie);
    const rt = soma(receita);
    const np = Math.max(1, serie.length);
    cab = [cel(tot, rt, 0), cel(tot / np, rt / np, 1)];
  }
  return cab.concat(ordem.map((j, k) => cel(serie[j], receita[j], n + k)));
};

// ------------------------------------------------------------------ peças visuais

function Botoes<T extends string>({
  opcoes,
  atual,
  onMudar,
  rotulo,
}: {
  opcoes: [T, string][];
  atual: T;
  onMudar: (k: T) => void;
  rotulo: string;
}) {
  return (
    <div role="group" aria-label={rotulo} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {opcoes.map(([k, nome]) => {
        const ativo = atual === k;
        return (
          <button
            key={k}
            type="button"
            aria-pressed={ativo}
            onClick={() => onMudar(k)}
            style={{
              padding: '5px 14px',
              borderRadius: 999,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              border: `1px solid ${ativo ? '#1B88F5' : '#E4E4E7'}`,
              background: ativo ? '#E9F4FD' : '#FFFFFF',
              color: ativo ? '#155FB0' : '#52525B',
            }}
          >
            {nome}
          </button>
        );
      })}
    </div>
  );
}

function Segmentado<T extends string>({
  opcoes,
  atual,
  onMudar,
  rotulo,
}: {
  opcoes: [T, string][];
  atual: T;
  onMudar: (k: T) => void;
  rotulo: string;
}) {
  return (
    <div
      role="group"
      aria-label={rotulo}
      style={{
        display: 'inline-flex',
        padding: 3,
        borderRadius: 10,
        background: 'rgba(15, 23, 42, .06)',
      }}
    >
      {opcoes.map(([k, nome]) => {
        const ativo = atual === k;
        return (
          <button
            key={k}
            type="button"
            aria-pressed={ativo}
            onClick={() => onMudar(k)}
            style={{
              padding: '4px 12px',
              border: 0,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              background: ativo ? '#FFFFFF' : 'transparent',
              color: ativo ? '#0F172A' : '#52525B',
              boxShadow: ativo ? '0 1px 3px rgba(15, 23, 42, .15)' : 'none',
            }}
          >
            {nome}
          </button>
        );
      })}
    </div>
  );
}

interface ILinhaDaTabela {
  chave: string;
  nome: string;
  forte: boolean;
  celulas: ICelula[];
  filhas?: { chave: string; nome: string; celulas: ICelula[] }[];
}

/** Tabela de várias colunas com a primeira fixa; linha com filhas abre e fecha. */
function TabelaDePeriodos({
  cabecas,
  linhas,
  largura,
}: {
  cabecas: { nome: string; fundo: string; cor: string }[];
  linhas: ILinhaDaTabela[];
  largura: string;
}) {
  const [abertas, setAbertas] = useState<string[]>([]);
  return (
    <div style={{ overflowX: 'auto', borderRadius: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: largura }}>
        <thead>
          <tr style={CABECA}>
            <th
              className="fixa"
              aria-label="Linha"
              style={{ minWidth: 250, background: '#F3F8FD' }}
            />
            {cabecas.map((c, j) => (
              <th
                // eslint-disable-next-line react/no-array-index-key
                key={j}
                style={{
                  padding: '7px 8px',
                  fontWeight: 700,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  background: c.fundo,
                  color: c.cor,
                }}
              >
                {c.nome}
              </th>
            ))}
          </tr>
        </thead>
        {linhas.map(l => {
          const abre = !!l.filhas?.length;
          const aberta = abre && abertas.includes(l.chave);
          const alternar = () =>
            setAbertas(aberta ? abertas.filter(x => x !== l.chave) : [...abertas, l.chave]);
          return (
            <tbody key={l.chave}>
              <tr
                className={abre ? 'abre' : undefined}
                style={{ borderTop: '1px solid #E6ECF2', fontWeight: l.forte ? 700 : 400 }}
              >
                <td
                  className="fixa"
                  style={{ padding: '6px 8px', background: l.forte ? '#EDF3F9' : '#F6F9FC' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {abre && (
                      <button
                        type="button"
                        onClick={alternar}
                        aria-expanded={aberta}
                        aria-label={`${aberta ? 'Fechar' : 'Abrir'} ${l.nome}`}
                        style={{
                          width: 18,
                          height: 18,
                          border: 0,
                          padding: 0,
                          background: 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#52525B"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          style={{ transform: `rotate(${aberta ? 90 : 0}deg)` }}
                        >
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    )}
                    <span style={{ paddingLeft: abre ? 0 : 22 }}>{l.nome}</span>
                  </div>
                </td>
                {l.celulas.map((c, j) => (
                  <td
                    // eslint-disable-next-line react/no-array-index-key
                    key={j}
                    style={{
                      padding: '6px 8px',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      color: c.cor,
                      background: c.fundo,
                    }}
                  >
                    {c.t}
                  </td>
                ))}
              </tr>
              {aberta &&
                l.filhas?.map(f => (
                  <tr key={f.chave} style={{ fontSize: 11.5 }}>
                    <td
                      className="fixa"
                      style={{ padding: '5px 8px 5px 34px', background: '#F1F5F9' }}
                    >
                      {f.nome}
                    </td>
                    {f.celulas.map((c, j) => (
                      <td
                        // eslint-disable-next-line react/no-array-index-key
                        key={j}
                        style={{
                          padding: '5px 8px',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          color: c.cor,
                          background: 'rgba(15, 23, 42, .025)',
                        }}
                      >
                        {c.t}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

/** Barras em cascata: total azul, positivo verde, negativo vermelho. */
function Cascata({
  passos,
  altura,
  folga,
}: {
  passos: [string, number, boolean?][];
  altura: number;
  folga: string;
}) {
  let acc = 0;
  const barras = passos.map(([r, v, total]) => {
    const ini = total ? 0 : acc;
    const fim = total ? v : acc + v;
    acc = fim;
    return { r, v, total, lo: Math.min(ini, fim), hi: Math.max(ini, fim) };
  });
  const max = Math.max(...barras.map(b => b.hi)) || 1;
  const corDa = (b: (typeof barras)[number]) => {
    if (b.total) return CORES[0];
    return b.v >= 0 ? CORES[1] : CORES[2];
  };
  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 8,
          height: altura,
          paddingTop: 8,
          borderBottom: '1px solid #D4DAE2',
        }}
      >
        {barras.map(b => (
          <div
            key={b.r}
            style={{ flex: '1 1 0', minWidth: 0, position: 'relative' }}
            title={`${b.r}: ${num(b.v)}`}
          >
            <span
              style={{
                position: 'absolute',
                left: folga,
                right: folga,
                bottom: `${((Math.max(0, b.lo) / max) * 100).toFixed(2)}%`,
                height: `${Math.max(0.6, ((b.hi - Math.max(0, b.lo)) / max) * 100).toFixed(2)}%`,
                borderRadius: 4,
                background: corDa(b),
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {barras.map(b => (
          <div
            key={b.r}
            style={{
              flex: '1 1 0',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 10.5, color: '#52525B', lineHeight: 1.2 }}>{b.r}</span>
            <b style={{ fontSize: 11 }}>{numCurto(b.v)}</b>
          </div>
        ))}
      </div>
    </>
  );
}

function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '24px 40px',
        border: '1px dashed #CBD5E1',
        borderRadius: 12,
        textAlign: 'center',
        fontSize: 13,
        color: '#52525B',
      }}
    >
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ DRE e EBITDA

function ResultadoComAjustes({
  secao,
  competencia,
}: {
  secao: IDemonstracoes | undefined;
  competencia: string;
}) {
  const auditada = secao?.dre_auditada ?? {};
  const mes = secao?.dre.mes ?? {};
  const dif = (k: string) => (auditada[k] || 0) - (mes[k] || 0);
  const linhas: [string, string, boolean][] = [
    ['receita_bruta', 'Receita bruta', false],
    ['receita_liquida', 'Receita líquida', true],
    ['custos', '(−) Custo das vendas', false],
    ['lucro_bruto', 'Lucro bruto', true],
    ['resultado_operacional', 'Resultado operacional', true],
    ['resultado_antes_ir', 'Resultado antes do IR/CSLL', true],
    ['ir_csll', '(−) IR/CSLL', false],
    ['lucro_liquido', 'Lucro líquido', true],
  ];
  const tem = Object.keys(auditada).length > 0;
  const efeito = dif('lucro_liquido');
  let corDoEfeito = '#52525B';
  if (efeito < 0) corDoEfeito = VERMELHO;
  else if (efeito > 0) corDoEfeito = '#15803D';
  return (
    <section aria-label="Resultado com os ajustes propostos" style={SECAO}>
      <h2 style={H2}>Resultado com os ajustes propostos · {mesLongo(competencia)}</h2>
      <span style={SUB}>
        Como o resultado do mês ficaria se os ajustes propostos fossem lançados. Proposta do
        sistema: nada foi gravado no Domínio.
      </span>
      {tem ? (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={CABECA}>
                <th aria-label="Linha" />
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>
                  Como está
                </th>
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>
                  Com ajustes
                </th>
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>
                  Diferença
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(([k, nome, forte]) => {
                const d = dif(k);
                let c = '#52525B';
                if (Math.abs(d) >= 0.01) c = d < 0 ? VERMELHO : '#15803D';
                return (
                  <tr
                    key={k}
                    style={{ borderTop: '1px solid #EEF2F6', fontWeight: forte ? 700 : 400 }}
                  >
                    <td style={{ padding: '6px 8px' }}>{nome}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{num(mes[k] || 0)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      {num(auditada[k] || 0)}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: c }}>
                      {Math.abs(d) >= 0.01 ? num(d) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <span style={SUB}>
            Efeito total no lucro do mês:{' '}
            <b style={{ color: corDoEfeito }}>{Math.abs(efeito) >= 0.01 ? num(efeito) : '—'}</b> ·
            detalhes em Ajustes propostos
          </span>
        </>
      ) : (
        <Vazio>Nenhum ajuste proposto muda o resultado de {mesLongo(competencia)}.</Vazio>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ balanço

const SECOES: Record<
  'ativo' | 'passivo',
  [string, Exclude<keyof IGruposDoBalanco, 'subgrupos'>][]
> = {
  ativo: [
    ['Circulante', 'ativo_circulante'],
    ['Não Circulante', 'ativo_nao_circulante'],
  ],
  passivo: [
    ['Circulante', 'passivo_circulante'],
    ['Não Circulante', 'passivo_nao_circulante'],
    ['Patrimônio Líquido', 'patrimonio_liquido'],
  ],
};

function ColunaDoBalanco({
  coluna,
  atual,
  anterior,
}: {
  coluna: 'ativo' | 'passivo';
  atual: IPosicao;
  anterior: IPosicao | null;
}) {
  const natureza = coluna === 'ativo' ? 'D' : 'C';
  const b = atual.balanco as IGruposDoBalanco;
  const ba = anterior?.balanco ?? null;
  const valor = (m: ILinhaDoModelo[] | undefined, secao: string, linha: string) =>
    m?.find(l => l.coluna === coluna && l.secao === secao && l.linha === linha)?.atual ?? null;
  const celula = (v: number | null | undefined) => {
    const s = saldoDoBalanco(v, natureza);
    return (
      <td
        style={{
          padding: '6px 8px',
          textAlign: 'right',
          color: s.contrario ? VERMELHO : undefined,
        }}
      >
        {s.texto}
      </td>
    );
  };
  const linha = (
    chave: string,
    nome: string,
    a: number | null | undefined,
    an: number | null | undefined,
    tipo: 'secao' | 'linha' | 'total',
  ) => {
    let fundo = 'transparent';
    if (tipo === 'total') fundo = 'rgba(27, 111, 208, .06)';
    else if (tipo === 'secao') fundo = 'rgba(15, 23, 42, .03)';
    return (
      <tr
        key={chave}
        style={{
          borderTop: '1px solid #EEF2F6',
          fontWeight: tipo === 'linha' ? 400 : 700,
          background: fundo,
        }}
      >
        <td style={{ padding: `6px 8px 6px ${tipo === 'linha' ? 22 : 8}px` }}>{nome}</td>
        {celula(a)}
        {celula(an)}
        <td
          style={{
            padding: '6px 8px',
            textAlign: 'right',
            fontSize: 12,
            color: '#52525B',
            fontWeight: 400,
          }}
        >
          {a != null && an != null ? num(a - an) : '—'}
        </td>
      </tr>
    );
  };
  const totalDoPassivo = (x: Partial<IGruposDoBalanco> | null) =>
    x?.passivo_total != null
      ? x.passivo_total + (x.patrimonio_liquido ?? 0) + (x.resultado_do_periodo ?? 0)
      : null;
  return (
    <div style={{ minWidth: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 480 }}>
        <thead>
          <tr style={{ ...CABECA, textAlign: 'left' }}>
            <th style={{ padding: '6px 8px', fontWeight: 600 }}>
              {coluna === 'ativo' ? 'Ativo' : 'Passivo e Patrimônio Líquido'}
            </th>
            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>
              {atual.rotulo}
            </th>
            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>
              {anterior?.rotulo ?? 'Anterior'}
            </th>
            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Variação</th>
          </tr>
        </thead>
        <tbody>
          {SECOES[coluna].map(([secao, chave]) => {
            const nomes: string[] = [];
            [atual.modelo, anterior?.modelo ?? []].forEach(m =>
              m.forEach(l => {
                if (l.coluna === coluna && l.secao === secao && !nomes.includes(l.linha))
                  nomes.push(l.linha);
              }),
            );
            return (
              <React.Fragment key={secao}>
                {linha(secao, secao, b[chave], ba?.[chave], 'secao')}
                {nomes.map(n =>
                  linha(
                    `${secao}|${n}`,
                    n,
                    valor(atual.modelo, secao, n) ?? 0,
                    anterior ? (valor(anterior.modelo, secao, n) ?? 0) : null,
                    'linha',
                  ),
                )}
              </React.Fragment>
            );
          })}
          {coluna === 'passivo' &&
            linha(
              'resultado',
              'Lucro ou Prejuízo líquido do período',
              b.resultado_do_periodo,
              ba?.resultado_do_periodo,
              'linha',
            )}
          {coluna === 'ativo'
            ? linha('total', 'Total do Ativo', b.ativo_total, ba?.ativo_total, 'total')
            : linha(
                'total',
                'Total do Passivo e do PL',
                totalDoPassivo(b),
                totalDoPassivo(ba),
                'total',
              )}
        </tbody>
      </table>
    </div>
  );
}

function Balanco({ c }: { c: IColunasDoPeriodo }) {
  const ultima = c.colunas[c.colunas.length - 1];
  const atual = ultima?.fim;
  if (!atual?.balanco) return <Vazio>Sem saldos para montar o balanço no fim do período.</Vazio>;
  const b = atual.balanco;
  const ba = c.anterior?.balanco ?? null;
  const rotA = atual.rotulo;
  const rotB = c.anterior?.rotulo ?? 'anterior';
  const total = b.ativo_total || 1;
  const parte = (nome: string, v: number, cr: string) => ({
    nome,
    v,
    cr,
    largura: `${Math.max(0, (v / total) * 100).toFixed(2)}%`,
  });
  const composicao = [
    {
      titulo: 'Aplicações (ativo)',
      partes: [
        parte('Ativo circulante', b.ativo_circulante, '#1B88F5'),
        parte('Ativo não circulante', b.ativo_nao_circulante, '#0891B2'),
      ],
    },
    {
      titulo: 'Origens (passivo + PL)',
      partes: [
        parte('Passivo circulante', b.passivo_circulante, '#DC2626'),
        parte('Passivo não circulante', b.passivo_nao_circulante, '#D97706'),
        parte('PL + resultado', b.patrimonio_liquido + b.resultado_do_periodo, '#16A34A'),
      ],
    },
  ];
  const contrarios = b.subgrupos.filter(s => s.valor < 0);
  const porGrupo: [string, keyof IGruposDoBalanco | 'soma', boolean][] = [
    ['Ativo circulante', 'ativo_circulante', true],
    ['Caixa e bancos', 'disponivel', false],
    ['Ativo não circulante', 'ativo_nao_circulante', true],
    ['Ativo total', 'ativo_total', true],
    ['Passivo circulante', 'passivo_circulante', true],
    ['Passivo não circulante', 'passivo_nao_circulante', true],
    ['Patrimônio líquido', 'patrimonio_liquido', true],
    ['Resultado do período', 'resultado_do_periodo', false],
    ['Passivo + PL + resultado', 'soma', true],
  ];
  const lerGrupo = (x: IGruposDoBalanco | null, k: keyof IGruposDoBalanco | 'soma') => {
    if (!x) return null;
    if (k === 'soma') return x.passivo_total + x.patrimonio_liquido + x.resultado_do_periodo;
    return x[k] as number;
  };
  return (
    <>
      <section aria-label="Balanço patrimonial" style={SECAO}>
        <h2 style={H2}>Balanço patrimonial · Ativo e Passivo lado a lado</h2>
        <span style={SUB}>
          Posição no fim de {rotA}, comparada com o fim de {rotB}. Layout da planilha da
          contabilidade.
        </span>
        <div className="g2" style={{ gap: 20 }}>
          <ColunaDoBalanco coluna="ativo" atual={atual} anterior={c.anterior} />
          <ColunaDoBalanco coluna="passivo" atual={atual} anterior={c.anterior} />
        </div>
      </section>
      <div className="g2">
        <section aria-label="Composição do balanço" style={SECAO}>
          <h2 style={H2}>Composição do balanço</h2>
          <span style={SUB}>
            De onde vêm os recursos (passivo e patrimônio) e onde estão aplicados (ativo).
          </span>
          {composicao.map(x => (
            <React.Fragment key={x.titulo}>
              <span style={{ ...SUB, marginTop: 4 }}>{x.titulo}</span>
              <div
                style={{
                  display: 'flex',
                  height: 30,
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#EEF2F6',
                }}
              >
                {x.partes.map(p => (
                  <span
                    key={p.nome}
                    title={`${p.nome}: ${num(p.v)} (${pct(p.v / total)})`}
                    style={{ width: p.largura, background: p.cr }}
                  />
                ))}
              </div>
            </React.Fragment>
          ))}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
            {composicao
              .flatMap(x => x.partes)
              .map(p => (
                <span
                  key={p.nome}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...SUB }}
                >
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: p.cr }} />
                  {p.nome}
                </span>
              ))}
          </div>
          {contrarios.length > 0 && (
            <div
              style={{
                marginTop: 6,
                padding: '8px 12px',
                borderLeft: '3px solid #D97706',
                borderRadius: 6,
                background: 'rgba(15, 23, 42, .03)',
                fontSize: 12.5,
              }}
            >
              Contas com saldo contrário à natureza distorcem a leitura:{' '}
              {contrarios
                .map(s => `${s.subgrupo.replace(/_/g, ' ')} ${num(Math.abs(s.valor))}`)
                .join(' · ')}
              .
            </div>
          )}
        </section>
        <section aria-label="Balanço por grupo" style={SECAO}>
          <h2 style={H2}>Balanço por grupo</h2>
          <span style={SUB}>
            Posição no fim de {rotA}, comparada com o fim de {rotB}.
          </span>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={CABECA}>
                <th aria-label="Grupo" />
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>{rotA}</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>{rotB}</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>
                  Variação
                </th>
              </tr>
            </thead>
            <tbody>
              {porGrupo.map(([nome, k, forte]) => {
                const a = lerGrupo(b, k);
                const an = lerGrupo(ba, k);
                return (
                  <tr
                    key={nome}
                    style={{ borderTop: '1px solid #EEF2F6', fontWeight: forte ? 700 : 400 }}
                  >
                    <td style={{ padding: `6px 8px 6px ${forte ? 8 : 26}px` }}>{nome}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{num(a)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{num(an)}</td>
                    <td
                      style={{
                        padding: '6px 8px',
                        textAlign: 'right',
                        fontSize: 12,
                        color: '#52525B',
                        fontWeight: 400,
                      }}
                    >
                      {a != null && an != null ? num(a - an) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <span style={SUB}>Ativo − (passivo + PL + resultado) = {num(b.diferenca)}</span>
        </section>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ fluxo de caixa

const LINHAS_DFC: [string, (d: Record<string, number>) => number, boolean][] = [
  ['Lucro líquido do período', d => d.lucro_liquido || 0, false],
  ['(+) Depreciação', d => d.depreciacao || 0, false],
  ['(±) Capital de giro', d => d.capital_de_giro || 0, false],
  ['Caixa das operações', d => d.operacional || 0, true],
  ['Caixa dos investimentos', d => d.investimento || 0, true],
  ['Caixa dos financiamentos', d => d.financiamento || 0, true],
  [
    'Não classificado',
    d =>
      (d.caixa_final || 0) -
      (d.caixa_inicial || 0) -
      (d.operacional || 0) -
      (d.investimento || 0) -
      (d.financiamento || 0),
    false,
  ],
  ['Caixa inicial', d => d.caixa_inicial || 0, false],
  ['Caixa final', d => d.caixa_final || 0, true],
];

function FluxoDeCaixa({ g }: { g: IGrade }) {
  const { c, multi, anos, ordem } = g;
  const ultima = c.colunas[c.colunas.length - 1];
  const d = ultima.dfc;
  const linhas = LINHAS_DFC.filter(
    ([nome, f]) => nome !== 'Não classificado' || c.colunas.some(x => Math.abs(f(x.dfc)) >= 0.01),
  );
  // na coluna da seleção: fluxos somados; caixa inicial do primeiro período e final do último
  const anosDfc = multi ? anos : [null];
  const selecao = (ano: number | null): Record<string, number> => {
    const cols = c.colunas.filter(x => ano == null || x.ano === ano);
    const s: Record<string, number> = {};
    cols.forEach(x =>
      Object.entries(x.dfc).forEach(([k, v]) => {
        s[k] = (s[k] || 0) + v;
      }),
    );
    s.caixa_inicial = cols[0]?.dfc.caixa_inicial ?? 0;
    s.caixa_final = cols[cols.length - 1]?.dfc.caixa_final ?? 0;
    return s;
  };
  const somas = anosDfc.map(selecao);
  const cabecas = anosDfc
    .map(a => ({ nome: a ? `Seleção ${a}` : 'Seleção', fundo: '#DCEBFB', cor: '#155FB0' }))
    .concat(
      ordem.map(j => ({
        nome: c.colunas[j].rotulo + (c.colunas[j].parcial ? '*' : ''),
        fundo: j === c.colunas.length - 1 ? '#EEF5FD' : 'transparent',
        cor: '#52525B',
      })),
    );
  return (
    <>
      <div className="g2">
        <section aria-label="Do caixa inicial ao caixa final" style={SECAO}>
          <h2 style={H2}>Do caixa inicial ao caixa final · {ultima.rotulo}</h2>
          <span style={SUB}>Fluxo de caixa gerencial do período, pelo método indireto.</span>
          <Cascata
            altura={240}
            folga="18%"
            passos={[
              ['Caixa inicial', d.caixa_inicial || 0, true],
              ['Operações', d.operacional || 0],
              ['Investimentos', d.investimento || 0],
              ['Financiamentos', d.financiamento || 0],
              ['Caixa final', d.caixa_final || 0, true],
            ]}
          />
        </section>
        <section aria-label="Fluxo de caixa do período" style={SECAO}>
          <h2 style={H2}>Fluxo de caixa · {ultima.rotulo}</h2>
          <span style={SUB}>
            Operações: o que o negócio gerou. Investimentos: compra e venda de bens. Financiamentos:
            empréstimos, sócios e capital.
          </span>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              {linhas.map(([nome, f, forte]) => (
                <tr
                  key={nome}
                  style={{
                    borderTop: '1px solid #EEF2F6',
                    fontWeight: forte ? 700 : 400,
                    background: forte ? 'rgba(15, 23, 42, .03)' : 'transparent',
                  }}
                >
                  <td style={{ padding: '7px 8px' }}>{nome}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', color: cor(f(d)) }}>
                    {num(f(d))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {Math.abs(d.sem_efeito_caixa || 0) >= 0.01 && (
            <span style={SUB}>
              Reclassificações entre contas sem passar pelo caixa ficaram fora dos fluxos:{' '}
              {num(d.sem_efeito_caixa)}.
            </span>
          )}
        </section>
      </div>
      <section aria-label="Fluxo de caixa por período" style={SECAO}>
        <h2 style={H2}>Fluxo de caixa por período</h2>
        <span style={SUB}>
          Na coluna da seleção, os fluxos somados; o caixa inicial é o do primeiro período e o
          final, o do último.
        </span>
        <TabelaDePeriodos
          largura={`${260 + 112 * (c.colunas.length + anosDfc.length)}px`}
          cabecas={cabecas}
          linhas={linhas.map(([nome, f, forte]) => ({
            chave: nome,
            nome,
            forte,
            celulas: somas
              .map(s => f(s))
              .concat(ordem.map(j => f(c.colunas[j].dfc)))
              .map((v, j) => ({
                t: num(v),
                cor: cor(v),
                fundo: j < anosDfc.length ? 'rgba(27, 111, 208, .07)' : 'transparent',
              })),
          }))}
        />
      </section>
    </>
  );
}

// ------------------------------------------------------------------ evolução

const redondo = (v: number) => {
  const e = 10 ** Math.floor(Math.log10(Math.max(v, 1)));
  const passo = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find(m => m * e >= v) || 10;
  return passo * e;
};

function Grafico({
  titulo,
  sub,
  series,
  min,
  max,
  formato,
  rotulos,
  largo,
}: {
  titulo: string;
  sub: string;
  series: [string, string, number[]][];
  min: number;
  max: number;
  formato: (v: number) => string;
  rotulos: string[];
  largo?: boolean;
}) {
  const n = rotulos.length;
  const pontos = (vals: number[]) =>
    vals
      .map(
        (v, i) =>
          `${(n === 1 ? 300 : (i / (n - 1)) * 600).toFixed(1)},${(
            200 -
            ((Math.min(max, Math.max(min, v)) - min) / (max - min || 1)) * 200
          ).toFixed(1)}`,
      )
      .join(' ');
  return (
    <section aria-label={titulo} className={largo ? 'span2' : undefined} style={SECAO}>
      <h2 style={H2}>{titulo}</h2>
      <span style={SUB}>{sub}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        <div
          style={{
            width: 64,
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            textAlign: 'right',
            fontSize: 10.5,
            color: '#52525B',
          }}
        >
          {[4, 3, 2, 1, 0].map(k => (
            <span key={k}>{formato(min + ((max - min) * k) / 4)}</span>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <svg
            viewBox="0 0 600 200"
            preserveAspectRatio="none"
            role="img"
            aria-label={titulo}
            style={{
              width: '100%',
              height: 200,
              backgroundImage: 'linear-gradient(#E4E4E7 1px, transparent 1px)',
              backgroundSize: '100% 50px',
            }}
          >
            {series.map(([nome, c, v]) => (
              <polyline
                key={nome}
                points={pontos(v)}
                fill="none"
                stroke={c}
                strokeWidth="2.4"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10.5,
              color: '#52525B',
            }}
          >
            {rotulos.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i}>{r}</span>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
        {series.map(([nome, c]) => (
          <span key={nome} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...SUB }}>
            <span style={{ width: 14, height: 3, borderRadius: 2, background: c }} />
            {nome}
          </span>
        ))}
      </div>
    </section>
  );
}

function Evolucao({ c, titulo }: { c: IColunasDoPeriodo; titulo: string }) {
  const cols = c.colunas;
  const n = cols.length;
  const v = (k: string) => cols.map(x => linhaDaDre(x.dre, k));
  const RL = v('receita_liquida');
  const pulo = n <= 14 ? 1 : Math.ceil(n / 12);
  const rotulos = cols.map((x, j) => (j % pulo === 0 || j === n - 1 ? x.rotulo : ''));
  const PARTES: [string, string, (j: number) => number][] = [
    ['Custo das vendas', '#F87171', j => v('custos')[j]],
    ['Pessoal', '#FBBF24', j => v('despesas_pessoal')[j]],
    [
      'Despesas operacionais e tributárias',
      '#A78BFA',
      j =>
        v('despesas_operacionais')[j] +
        v('despesas_tributarias')[j] +
        v('outras_despesas')[j] +
        v('outras_receitas')[j],
    ],
    [
      'Financeiro, depreciação e IR/CSLL',
      '#94A3B8',
      j =>
        v('receitas_financeiras')[j] +
        v('despesas_financeiras')[j] +
        v('depreciacao')[j] +
        v('ir_csll')[j],
    ],
  ];
  const saidas = (j: number) => -soma(PARTES.map(([, , f]) => f(j)));
  const ESC = redondo(Math.max(1, ...cols.map((_, j) => Math.max(RL[j], saidas(j)))) * 1.05);
  const y = (val: number) => 150 - (val / ESC) * 150;
  const passoX = 900 / n;
  const barras: { x: string; w: string; y: string; h: string; cor: string }[] = [];
  cols.forEach((_, j) => {
    const x = (j * passoX + passoX * 0.22).toFixed(1);
    const w = (passoX * 0.56).toFixed(1);
    barras.push({
      x,
      w,
      y: y(Math.max(0, RL[j])).toFixed(1),
      h: Math.abs(y(0) - y(RL[j])).toFixed(1),
      cor: '#60A5FA',
    });
    let cur = 0;
    PARTES.forEach(([, cr, f]) => {
      const val = f(j);
      const topo = y(cur);
      const base = y(cur + val);
      barras.push({
        x,
        w,
        y: Math.min(topo, base).toFixed(1),
        h: Math.abs(base - topo).toFixed(1),
        cor: cr,
      });
      cur += val;
    });
  });
  // lucro acumulado recomeça a cada ano
  let acum = 0;
  let anoAcum: number | null = null;
  const cx = (j: number) => (j * passoX + passoX / 2).toFixed(1);
  const LL = v('lucro_liquido');
  const ptsMes = LL.map((val, j) => `${cx(j)},${y(val).toFixed(1)}`).join(' ');
  const ptsAcum = LL.map((val, j) => {
    if (cols[j].ano !== anoAcum) {
      acum = 0;
      anoAcum = cols[j].ano;
    }
    acum += val;
    return `${cx(j)},${Math.max(-20, y(acum)).toFixed(1)}`;
  }).join(' ');
  const legenda: [string, string, boolean][] = [
    ['Receita líquida', '#60A5FA', true] as [string, string, boolean],
    ...PARTES.map(([nome, cr]) => [nome, cr, true] as [string, string, boolean]),
    ['Lucro do período', '#0F172A', false],
    ['Lucro acumulado no ano', '#16A34A', false],
  ];

  const margem = (serie: number[]) => serie.map((val, j) => (RL[j] ? val / RL[j] : 0));
  const margens = [margem(v('lucro_bruto')), margem(cols.map(x => ebitdaDe(x.dre))), margem(LL)];
  const maxMargem = Math.max(0.4, Math.ceil(Math.max(...margens.flat()) * 10) / 10);
  const minMargem = Math.min(0, Math.floor(Math.min(...margens.flat()) * 10) / 10);
  const caixa = cols.map(x => x.fim?.balanco?.disponivel ?? 0);
  const pl = cols.map(x =>
    x.fim?.balanco ? x.fim.balanco.patrimonio_liquido + x.fim.balanco.resultado_do_periodo : 0,
  );
  const maxCp = redondo(Math.max(1, ...caixa, ...pl) * 1.05);
  const minCp = Math.min(0, ...caixa, ...pl);
  const despesas: [string, string, number[]][] = [
    ['Custo das vendas', '#DC2626', v('custos').map(x => -x)],
    ['Pessoal', '#D97706', v('despesas_pessoal').map(x => -x)],
    ['Operacionais', '#7C3AED', v('despesas_operacionais').map(x => -x)],
    ['Tributárias', '#64748B', v('despesas_tributarias').map(x => -x)],
  ];
  const maxDesp = redondo(Math.max(1, ...despesas.flatMap(d => d[2])) * 1.05);

  return (
    <>
      <section aria-label="Composição do resultado" style={SECAO}>
        <h2 style={H2}>Composição do resultado</h2>
        <span style={SUB}>
          Acima da linha do zero, a receita líquida de cada período; abaixo, para onde ela foi. As
          linhas mostram o lucro do período e o lucro acumulado no ano · {titulo}.
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <div
            style={{
              width: 64,
              height: 300,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              textAlign: 'right',
              fontSize: 10.5,
              color: '#52525B',
            }}
          >
            {[ESC, ESC / 2, 0, -ESC / 2, -ESC].map(e => (
              <span key={e}>{numCurto(e)}</span>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <svg
              viewBox="0 0 900 300"
              preserveAspectRatio="none"
              role="img"
              aria-label="Composição do resultado por período"
              style={{
                width: '100%',
                height: 300,
                backgroundImage: 'linear-gradient(#E4E4E7 1px, transparent 1px)',
                backgroundSize: '100% 75px',
              }}
            >
              <line
                x1="0"
                y1="150"
                x2="900"
                y2="150"
                stroke="#94A3B8"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              {barras.map((b, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} fill={b.cor} />
              ))}
              <polyline
                points={ptsMes}
                fill="none"
                stroke="#0F172A"
                strokeWidth="2.6"
                vectorEffect="non-scaling-stroke"
              />
              <polyline
                points={ptsAcum}
                fill="none"
                stroke="#16A34A"
                strokeWidth="2.6"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                fontSize: 10.5,
                color: '#52525B',
              }}
            >
              {rotulos.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={i}>{r}</span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
          {legenda.map(([nome, cr, barra]) => (
            <span
              key={nome}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...SUB }}
            >
              <span
                style={{
                  width: barra ? 10 : 14,
                  height: barra ? 10 : 3,
                  borderRadius: 2,
                  background: cr,
                }}
              />
              {nome}
            </span>
          ))}
        </div>
      </section>
      <div className="g2">
        <Grafico
          titulo="Margens"
          sub="Lucro bruto, EBITDA e lucro líquido sobre a receita líquida, período a período."
          series={[
            ['Margem bruta', '#1B88F5', margens[0]],
            ['Margem EBITDA', '#D97706', margens[1]],
            ['Margem líquida', '#16A34A', margens[2]],
          ]}
          min={minMargem}
          max={maxMargem}
          formato={pct}
          rotulos={rotulos}
        />
        <Grafico
          titulo="Caixa e patrimônio"
          sub="Saldo de caixa e bancos e patrimônio líquido (com o resultado do período) no fim de cada período."
          series={[
            ['Caixa e bancos', '#0891B2', caixa],
            ['Patrimônio líquido', '#7C3AED', pl],
          ]}
          min={minCp}
          max={maxCp}
          formato={numCurto}
          rotulos={rotulos}
        />
        <Grafico
          titulo="Despesas por natureza"
          sub="Custos e despesas de cada período (valores positivos)."
          series={despesas}
          min={0}
          max={maxDesp}
          formato={numCurto}
          rotulos={rotulos}
          largo
        />
      </div>
    </>
  );
}

// ------------------------------------------------------------------ tela

const MOTIVOS_RESUMIDOS = (faltam: IColunasDoPeriodo['faltam']) => {
  const porMotivo = new Map<string, string[]>();
  faltam.forEach(f => porMotivo.set(f.motivo, [...(porMotivo.get(f.motivo) ?? []), f.rotulo]));
  return Array.from(porMotivo.entries()).map(
    ([motivo, rotulos]) => `${rotulos.join(', ')}: ${motivo}`,
  );
};

/**
 * Demonstrações do período no layout de 10/2026: as colunas seguem a periodicidade do
 * botão Período (personalizado = mês a mês). Tudo vem do que a revisão gravou.
 */
export function AbaDemonstracoes({
  empresaId,
  periodo,
  plano,
  secao,
  competencia,
}: {
  empresaId: number;
  periodo: IPeriodo;
  plano: Plano;
  /** a seção do mês da tela: traz a DRE com os ajustes propostos */
  secao: IDemonstracoes | undefined;
  competencia: string;
}) {
  const [aba, setAba] = useState<Aba>('dre');
  const [modo, setModo] = useState<Modo>('valor');
  const mm = (h: number) => String(Math.floor(h / 2) + 1).padStart(2, '0');
  const consultas = useDemonstracoesDosAnos(
    empresaId,
    periodo.anos,
    mm(periodo.h1),
    mm(periodo.h2),
  );
  const carregando = consultas.some(q => q.isLoading);
  const erro = consultas.find(q => q.isError);
  const porAno = useMemo(() => {
    const saida: Record<number, IDemonstracoesDoPeriodo | undefined> = {};
    periodo.anos.forEach((a, i) => {
      saida[a] = consultas[i]?.data;
    });
    return saida;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo.anos.join(','), ...consultas.map(q => q.dataUpdatedAt)]);
  const c = useMemo(() => montarColunas(periodo, porAno), [periodo, porAno]);

  if (carregando) return <Vazio>Carregando as demonstrações do período…</Vazio>;
  if (erro) return <Vazio>Não foi possível carregar as demonstrações do período.</Vazio>;
  const faltas = MOTIVOS_RESUMIDOS(c.faltam);
  if (!c.colunas.length)
    return (
      <Vazio>
        Sem dados para o período escolhido.
        {faltas.map(f => (
          <div key={f} style={{ marginTop: 4, fontSize: 12 }}>
            {f}
          </div>
        ))}
      </Vazio>
    );

  const g = montarGrade(c, periodo);
  const { colunas } = c;
  const nP = colunas.length;
  const ultima = colunas[nP - 1];
  const rot = (x: IColuna) => x.rotulo + (x.parcial ? '*' : '');
  const nomeVisao = (PERIODICIDADES.find(([k]) => k === c.visao)?.[1] ?? '').toLowerCase();
  const periodoTitulo = `${nP > 1 ? `${rot(colunas[0])} a ${rot(ultima)}` : rot(ultima)} · ${nomeVisao}`;
  const notas = [`${nP} ${nP === 1 ? 'período' : 'períodos'}`];
  if (colunas.some(x => x.parcial))
    notas.push('* parcial: o intervalo não cobre o agrupamento inteiro');
  if (c.visao === 'quinzenal') notas.push('1ª quinzena: dias 1 a 15');
  if (g.multi)
    notas.push('anos lado a lado, sem somar; a variação usa só os meses que existem nos dois anos');
  const cabecas = g.cabecas.concat(
    g.ordem.map(j => ({
      nome: rot(colunas[j]),
      fundo: j === nP - 1 ? '#EEF5FD' : 'transparent',
      cor: '#52525B',
    })),
  );
  const receita = colunas.map(x => linhaDaDre(x.dre, 'receita_bruta'));
  const receitaPorPedaco = (p: IPedaco) => linhaDaDre(p.dre, 'receita_bruta');

  // contas de cada linha da DRE, com o nome do plano
  const contasDaLinha = new Map<string, { codigo: string; nome: string }[]>();
  periodo.anos.forEach(a =>
    (porAno[a]?.dados?.dre_contas ?? []).forEach(conta => {
      const lista = contasDaLinha.get(conta.linha) ?? [];
      if (!lista.some(x => x.codigo === conta.codigo))
        lista.push({ codigo: conta.codigo, nome: textoDaContaOu(conta.codigo, plano, conta.nome) });
      contasDaLinha.set(conta.linha, lista);
    }),
  );
  const algumValor = (k: string) => colunas.some(x => Math.abs(linhaDaDre(x.dre, k)) >= 0.01);
  const linhasDre: ILinhaDaTabela[] = DRE.filter(
    ([k]) => k !== 'outras_receitas' || algumValor(k),
  ).map(([k, nome, forte]) => {
    const contas = (contasDaLinha.get(k) ?? [])
      .map(conta => ({ ...conta, total: soma(colunas.map(x => x.contas[conta.codigo] || 0)) }))
      .filter(conta => Math.abs(conta.total) >= 0.01)
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    return {
      chave: k,
      nome,
      forte,
      celulas: celulasDaLinha(
        g,
        colunas.map(x => linhaDaDre(x.dre, k)),
        receita,
        modo,
        p => linhaDaDre(p.dre, k),
        receitaPorPedaco,
      ),
      filhas: contas.map(conta => ({
        chave: conta.codigo,
        nome: conta.nome,
        celulas: celulasDaLinha(
          g,
          colunas.map(x => x.contas[conta.codigo] || 0),
          receita,
          modo,
          p => p.contas[conta.codigo] || 0,
          receitaPorPedaco,
        ),
      })),
    };
  });

  const L = (k: string) => linhaDaDre(ultima.dre, k);
  const linhasEbitda: [string, (d: Record<string, number>) => number, boolean][] = [
    ['Lucro líquido', d => linhaDaDre(d, 'lucro_liquido'), false],
    ['(+) IR/CSLL', d => -linhaDaDre(d, 'ir_csll'), false],
    ['(−) Receitas financeiras', d => -linhaDaDre(d, 'receitas_financeiras'), false],
    ['(+) Despesas financeiras', d => -linhaDaDre(d, 'despesas_financeiras'), false],
    ['(+) Depreciação e amortização', d => -linhaDaDre(d, 'depreciacao'), false],
    ['(=) EBITDA', ebitdaDe, true],
  ];
  const receitaLiquida = colunas.map(x => linhaDaDre(x.dre, 'receita_liquida'));
  const ebitda = colunas.map(x => ebitdaDe(x.dre));
  const tabelaEbitda: ILinhaDaTabela[] = linhasEbitda.map(([nome, f, forte]) => ({
    chave: nome,
    nome,
    forte,
    celulas: celulasDaLinha(
      g,
      colunas.map(x => f(x.dre)),
      receita,
      'valor',
      p => f(p.dre),
      receitaPorPedaco,
      v => num(v),
    ),
  }));
  const margemPor = (e: number, r: number) => (r ? pct(e / r) : '—');
  const margemCab: ICelula[] = g.multi
    ? g.anos
        .map(a => {
          const e = soma(colunas.map((x, j) => (x.ano === a ? ebitda[j] : 0)));
          const r = soma(colunas.map((x, j) => (x.ano === a ? receitaLiquida[j] : 0)));
          return { t: margemPor(e, r), cor: '#52525B' };
        })
        .concat([
          (() => {
            const [a, b] = g.anos;
            const tc = (ano: number, f: (p: IPedaco) => number) =>
              totalComparavel(colunas, ano, c.mesesComuns, f);
            const ma =
              tc(a, p => ebitdaDe(p.dre)) / (tc(a, p => linhaDaDre(p.dre, 'receita_liquida')) || 1);
            const mb =
              tc(b, p => ebitdaDe(p.dre)) / (tc(b, p => linhaDaDre(p.dre, 'receita_liquida')) || 1);
            return varPP((ma - mb) * 100);
          })(),
        ])
    : [
        { t: margemPor(soma(ebitda), soma(receitaLiquida)), cor: '#52525B' },
        { t: margemPor(soma(ebitda), soma(receitaLiquida)), cor: '#52525B' },
      ];
  tabelaEbitda.push({
    chave: 'margem',
    nome: 'Margem EBITDA (sobre a receita líquida)',
    forte: false,
    celulas: margemCab
      .concat(g.ordem.map(j => ({ t: margemPor(ebitda[j], receitaLiquida[j]), cor: '#52525B' })))
      .map((x, j) => ({ ...x, fundo: g.fundo(j) })),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Botoes rotulo="Demonstração" opcoes={ABAS} atual={aba} onMudar={setAba} />
      {faltas.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderLeft: '3px solid #D97706',
            borderRadius: 6,
            background: 'rgba(255, 255, 255, .62)',
            fontSize: 12.5,
          }}
        >
          Fora das colunas — {faltas.join(' · ')}.
        </div>
      )}

      {aba === 'dre' && (
        <>
          <section aria-label="Demonstração do resultado" style={SECAO}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <h2 style={H2}>Demonstração do resultado · {periodoTitulo}</h2>
                <span style={SUB}>
                  {g.multi
                    ? `Total de cada ano, variação entre ${g.anos[0]} e ${g.anos[1]}`
                    : `Acumulado da seleção, ${MEDIA[c.visao].toLowerCase()}`}{' '}
                  e cada período ({notas.join(' · ')}). Despesas entre parênteses. Clique numa linha
                  com seta para ver as contas.
                </span>
              </div>
              <Segmentado
                rotulo="Mostrar como"
                opcoes={[
                  ['valor', 'Valores'],
                  ['pct', '% da receita bruta'],
                ]}
                atual={modo}
                onMudar={setModo}
              />
            </div>
            <TabelaDePeriodos cabecas={cabecas} linhas={linhasDre} largura={g.largura} />
          </section>
          <div className="g2">
            <section aria-label="Da receita ao lucro" style={SECAO}>
              <h2 style={H2}>Da receita ao lucro · {rot(ultima)}</h2>
              <span style={SUB}>
                Cada barra mostra quanto cada grupo somou (verde) ou tirou (vermelho) do resultado.
              </span>
              <Cascata
                altura={220}
                folga="14%"
                passos={[
                  ['Receita bruta', L('receita_bruta'), true],
                  ['Deduções', L('deducoes')],
                  ['Custos', L('custos')],
                  ['Pessoal', L('despesas_pessoal')],
                  [
                    'Despesas operacionais',
                    L('despesas_operacionais') + L('outras_despesas') + L('outras_receitas'),
                  ],
                  ['Despesas tributárias', L('despesas_tributarias')],
                  [
                    'Financeiro e depreciação',
                    L('depreciacao') +
                      L('receitas_financeiras') +
                      L('despesas_financeiras') +
                      L('ir_csll'),
                  ],
                  ['Lucro líquido', L('lucro_liquido'), true],
                ]}
              />
            </section>
            <ResultadoComAjustes secao={secao} competencia={competencia} />
          </div>
        </>
      )}

      {aba === 'ebitda' && (
        <section aria-label="EBITDA" style={SECAO}>
          <h2 style={H2}>EBITDA · {periodoTitulo}</h2>
          <span style={SUB}>
            Lucro antes dos juros, do IR/CSLL, da depreciação e da amortização: quanto a operação
            gera, sem o efeito de como a empresa se financia e dos impostos sobre o lucro.
          </span>
          <TabelaDePeriodos cabecas={cabecas} linhas={tabelaEbitda} largura={g.largura} />
        </section>
      )}

      {aba === 'bp' && <Balanco c={c} />}
      {aba === 'dfc' && <FluxoDeCaixa g={g} />}
      {aba === 'evol' && <Evolucao c={c} titulo={periodoTitulo} />}
    </div>
  );
}
